import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InfopankCompanySearchResponseDto, InfopankPersonSearchResponseDto } from '@/apiTypes/types';
import { IMarketingListToolFilter } from '@/components/content/MarketingListTool/interfaces/marketingListToolFilter.interface';
import { DEBOUNCE_DELAY } from '@/lib/constants';
import { useTRPC } from '@/trpc/client';
import useLocale from '@/lib/hooks/useLocale';
import { AuthContext } from '@/components/auth/AuthProvider';
import {
  companyDataQueryOptions,
  companyFiltersQueryOptions,
  personDataQueryOptions,
  personFiltersQueryOptions,
} from '@/lib/queries/marketingQueries';

const emptyPersonResult: InfopankPersonSearchResponseDto = {
  items: [],
  meta: {
    total: 0,
    pageNumber: 0,
    pageSize: 50,
    links: [],
  },
};

const emptyCompanyResult: InfopankCompanySearchResponseDto = {
  items: [],
  meta: {
    total: 0,
    pageNumber: 0,
    pageSize: 50,
    links: [],
  },
};

const useMarketingListFilter = (currentTab: number, form: IMarketingListToolFilter) => {
  const trpc = useTRPC();
  const locale = useLocale();
  const { initialized, orderInitialized } = useContext(AuthContext);
  const [currentPage, setCurrentPage] = useState(0);
  const authReady = initialized && orderInitialized;

  const [debouncedForm, setDebouncedForm] = useState(form);
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedForm(form), DEBOUNCE_DELAY);
    return () => clearTimeout(timeout);
  }, [form]);

  const personFiltersQuery = useQuery(personFiltersQueryOptions({ trpc, locale, enabled: currentTab === 1 }));
  const companyFiltersQuery = useQuery(companyFiltersQueryOptions({ trpc, locale, enabled: currentTab === 2 }));

  const personDataQuery = useQuery(
    personDataQueryOptions({ trpc, filter: debouncedForm, page: currentPage, enabled: authReady && currentTab === 1 }),
  );
  const companyDataQuery = useQuery(
    companyDataQueryOptions({ trpc, filter: debouncedForm, page: currentPage, enabled: authReady && currentTab === 2 }),
  );

  const activeDataQuery = currentTab === 1 ? personDataQuery : companyDataQuery;
  const activeFiltersQuery = currentTab === 1 ? personFiltersQuery : companyFiltersQuery;

  return {
    personFilters: personFiltersQuery.data ?? null,
    companyFilters: companyFiltersQuery.data ?? null,
    personResult: personDataQuery.data ?? emptyPersonResult,
    companyResult: companyDataQuery.data ?? emptyCompanyResult,
    isTotalLoaded: activeDataQuery.isSuccess && !activeDataQuery.isPlaceholderData,
    error: activeDataQuery.error ?? activeFiltersQuery.error,
    isError: activeDataQuery.isError || activeFiltersQuery.isError,
    setCurrentPage,
  };
};

export default useMarketingListFilter;
